import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import styles from './Captcha.module.css';

interface CaptchaProps {
  onVerify: (isValid: boolean) => void;
  resetTrigger?: number;
  requiredFieldsFilled?: boolean;
}

export function Captcha({ onVerify, resetTrigger, requiredFieldsFilled = true }: CaptchaProps) {
  const [captchaCode, setCaptchaCode] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showFieldsError, setShowFieldsError] = useState(false);

  const generateCaptcha = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setCaptchaCode(code);
    setUserInput('');
    setIsValid(false);
    setShowError(false);
    onVerify(false);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  // Reset when parent triggers (after form submission)
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      generateCaptcha();
    }
  }, [resetTrigger]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!requiredFieldsFilled) {
      setShowFieldsError(true);
      setTimeout(() => setShowFieldsError(false), 3000);
      return;
    }

    const value = e.target.value;
    setUserInput(value);
    setShowError(false);
    
    const isCorrect = value === captchaCode;
    setIsValid(isCorrect);
    onVerify(isCorrect);

    // Auto-refresh if wrong and user completed typing
    if (value.length === 6 && !isCorrect) {
      setShowError(true);
      setTimeout(() => {
        generateCaptcha();
      }, 1200);
    }
  };

  const handleFocus = () => {
    if (!requiredFieldsFilled) {
      setShowFieldsError(true);
      setTimeout(() => setShowFieldsError(false), 3000);
    }
  };
  
  return (
    <div className={styles.captchaContainer}>
      <label className={styles.label}>Verify you're human</label>
      <div className={styles.captchaContent}>
        <div className={styles.captchaDisplay}>
          {captchaCode.split('').map((char, index) => (
            <motion.span
              key={`${captchaCode}-${index}`}
              className={styles.captchaChar}
              style={{
                transform: `rotate(${Math.random() * 20 - 10}deg) translateY(${Math.random() * 4 - 2}px)`,
                color: `hsl(${Math.random() * 60 + 10}, 70%, ${Math.random() * 20 + 40}%)`
              }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05, type: "spring" }}
            >
              {char}
            </motion.span>
          ))}
        </div>
        <div className={styles.inputGroup}>
          <input
            type="text"
            value={userInput}
            onChange={handleChange}
            onFocus={handleFocus}
            className={`${styles.input} ${isValid ? styles.valid : showError ? styles.invalid : ''} ${!requiredFieldsFilled ? styles.disabled : ''}`}
            placeholder="Enter code above"
            maxLength={6}
            required
            disabled={!requiredFieldsFilled}
          />
          <motion.button
            type="button"
            onClick={generateCaptcha}
            className={styles.refreshButton}
            whileHover={{ scale: 1.1, rotate: 180 }}
            whileTap={{ scale: 0.9 }}
            title="Generate new code"
          >
            <RefreshCw size={16} />
          </motion.button>
        </div>
        {showFieldsError && (
          <motion.div
            className={styles.warningBadge}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            ⚠ Please fill in Name, Email, and Message first!
          </motion.div>
        )}
        {isValid && (
          <motion.div
            className={styles.successBadge}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            ✓ Verified
          </motion.div>
        )}
        {showError && (
          <motion.div
            className={styles.errorBadge}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            ✗ Incorrect - Generating new code...
          </motion.div>
        )}
      </div>
    </div>
  );
}